import { snapMsToNeighbours, type DropNeighbour } from './useAgendaLayout.js'

/**
 * Dépose d'une sélection glissée d'un seul geste.
 *
 * Le groupe se déplace **rigidement** : un seul écart pour tous ses membres.
 * Les règles sont celles de la dépose d'un élément seul (`resolveDropStartMs`),
 * appliquées au groupe entier :
 *
 *   1. *aimantage* — le bord d'un membre à moins de `magnetMs` d'un voisin s'y
 *      colle ; sinon l'élément saisi retombe sur le pas de la grille ;
 *   2. *non-recouvrement* — aucun membre ne se pose sur un élément resté en
 *      place. En cas de heurt, l'écart est reporté au plus près pour que le
 *      membre fautif se colle avant ou après ce qu'il heurtait.
 *
 * Les éléments engagés à l'antenne font partie des voisins, pas du groupe :
 * sélectionnés ou non, ils ne bougent pas.
 *
 * Retourne `null` quand aucun écart proche ne laisse la place au groupe.
 */
export interface GroupDropMember {
  startMs: number
  endMs:   number
}

export function resolveGroupDropDeltaMs(input: {
  members:       GroupDropMember[]
  /** Départ de l'élément saisi : c'est lui qui tombe sur le pas de la grille. */
  anchorStartMs: number
  rawDeltaMs:    number
  neighbours:    DropNeighbour[]
  dayStartMs:    number
  snapMinutes:   number
  magnetMs:      number
}): number | null {
  const { members, anchorStartMs, rawDeltaMs, neighbours, dayStartMs } = input
  if (members.length === 0) return null

  const snapMs = Math.max(1_000, Math.round(input.snapMinutes * 60_000))
  const anchorTarget = dayStartMs + Math.round((anchorStartMs + rawDeltaMs - dayStartMs) / snapMs) * snapMs
  let delta = anchorTarget - anchorStartMs

  // L'aimant le plus proche de la trajectoire brute l'emporte, quel que soit
  // le membre qui le trouve : c'est le bord que l'opérateur est en train de viser.
  let bestMagnet: number | null = null
  for (const member of members) {
    const snapped = snapMsToNeighbours(member.startMs + rawDeltaMs, member.endMs - member.startMs, neighbours, input.magnetMs)
    if (snapped === null) continue
    const candidate = snapped - member.startMs
    if (bestMagnet === null || Math.abs(candidate - rawDeltaMs) < Math.abs(bestMagnet - rawDeltaMs)) bestMagnet = candidate
  }
  if (bestMagnet !== null) delta = bestMagnet

  const firstStart = Math.min(...members.map(member => member.startMs))
  const minDelta = dayStartMs - firstStart
  delta = Math.max(delta, minDelta)

  const hits = (candidate: number) => members.flatMap(member => neighbours
    .filter(n => member.startMs + candidate < n.endMs && n.startMs < member.endMs + candidate)
    .map(n => ({ member, n })))

  const collisions = hits(delta)
  if (collisions.length === 0) return delta

  const free = collisions
    .flatMap(({ member, n }) => [n.startMs - member.endMs, n.endMs - member.startMs])
    .filter(candidate => candidate >= minDelta && hits(candidate).length === 0)
    .sort((a, b) => Math.abs(a - rawDeltaMs) - Math.abs(b - rawDeltaMs))

  return free.length > 0 ? free[0] : null
}
